/**
 * Accent + color-scheme preference. One localStorage key, `d12-theme`.
 * Legacy values ("light" | "dark" | "system") are a color scheme with the
 * lavender accent. "lavender" | "ember" alone are an accent on the dark scheme.
 * A JSON object stores both.
 *
 * The no-flash script in index.html mirrors `parseStoredTheme` — keep them
 * in step. Lavender is the default; ember is opt-in.
 */

export const THEME_STORAGE_KEY = "d12-theme";

export type ThemeMode = "system" | "light" | "dark";
export type AccentTheme = "lavender" | "ember";
export type StoredTheme = { mode: ThemeMode; accent: AccentTheme };

const MODES = new Set<ThemeMode>(["system", "light", "dark"]);
const ACCENTS = new Set<AccentTheme>(["lavender", "ember"]);

function isMode(value: unknown): value is ThemeMode {
	return typeof value === "string" && MODES.has(value as ThemeMode);
}

function isAccent(value: unknown): value is AccentTheme {
	return typeof value === "string" && ACCENTS.has(value as AccentTheme);
}

export function parseStoredTheme(raw: string | null): StoredTheme {
	const fallback: StoredTheme = { mode: "dark", accent: "lavender" };
	if (!raw) return fallback;
	if (isMode(raw)) return { mode: raw, accent: "lavender" };
	if (isAccent(raw)) return { mode: "dark", accent: raw };
	if (raw.startsWith("{")) {
		try {
			const parsed = JSON.parse(raw) as { mode?: unknown; accent?: unknown };
			return {
				mode: isMode(parsed.mode) ? parsed.mode : "dark",
				accent: isAccent(parsed.accent) ? parsed.accent : "lavender",
			};
		} catch {
			return fallback;
		}
	}
	return fallback;
}

export function readStoredTheme(): StoredTheme {
	if (typeof window === "undefined") return { mode: "dark", accent: "lavender" };
	return parseStoredTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
}

export function writeStoredTheme(next: StoredTheme): void {
	window.localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(next));
}

function systemIsLight(): boolean {
	return window.matchMedia("(prefers-color-scheme: light)").matches;
}

/** Light sets data-theme="light". Dark clears only that value, so an explicit
 * data-theme="ember" toggle is left alone. */
export function applyMode(mode: ThemeMode): void {
	const el = document.documentElement;
	const light = mode === "system" ? systemIsLight() : mode === "light";
	if (light) el.setAttribute("data-theme", "light");
	else if (el.getAttribute("data-theme") === "light") el.removeAttribute("data-theme");
}

export function applyAccent(accent: AccentTheme): void {
	const el = document.documentElement;
	if (accent === "ember") el.setAttribute("data-accent", "ember");
	else el.removeAttribute("data-accent");
}

export function getMode(): ThemeMode {
	return readStoredTheme().mode;
}

export function setMode(mode: ThemeMode): void {
	writeStoredTheme({ ...readStoredTheme(), mode });
	applyMode(mode);
}

export function getAccent(): AccentTheme {
	return readStoredTheme().accent;
}

export function setAccent(accent: AccentTheme): void {
	writeStoredTheme({ ...readStoredTheme(), accent });
	applyAccent(accent);
}
